import { AUTHZ_FEATURES } from '../../../constants/authz/features.constants';
import { getEffectiveRecordActions, campaignHasExplicitRecordGrants } from '../../../authorization/hasRecordAction';

const RECORD = AUTHZ_FEATURES.RECORDS;

export const RECORD_CRUD_KEYS = [
  { key: RECORD.VIEW, labelKey: 'view' },
  { key: RECORD.CREATE, labelKey: 'create' },
  { key: RECORD.UPDATE, labelKey: 'update' },
  { key: RECORD.DELETE, labelKey: 'delete' },
];

export const RECORD_FILE_ACTION_KEYS = [
  { key: RECORD.DOWNLOAD, labelKey: 'download' },
  { key: RECORD.IMPORT, labelKey: 'import' },
  { key: RECORD.MOVE, labelKey: 'move' },
];

export const RECORD_ROW_ACTION_KEYS = [
  { key: RECORD.ASSIGN, labelKey: 'assign', crossModule: null },
  { key: RECORD.VIEW_STATUS_LOGS, labelKey: 'statusLogs', crossModule: null },
  { key: RECORD.SEND_TEMPLATE, labelKey: 'sendTemplate', crossModule: 'whatsapp' },
  { key: RECORD.VIEW_IMAGES, labelKey: 'images', crossModule: null },
  { key: RECORD.QUICK_CALL, labelKey: 'quickCall', crossModule: null },
  { key: RECORD.CHAT, labelKey: 'chat', crossModule: null },
  { key: RECORD.MANAGE_ADMIN, labelKey: 'manageRecord', crossModule: null },
  { key: RECORD.ADD_COMMENT, labelKey: 'addComment', crossModule: null },
];

export const RECORD_PRESETS = {
  readOnly: {
    crud: [RECORD.VIEW],
    fileActions: [RECORD.DOWNLOAD],
    rowActions: [RECORD.VIEW_STATUS_LOGS, RECORD.VIEW_IMAGES],
  },
  agent: {
    crud: [RECORD.VIEW, RECORD.CREATE, RECORD.UPDATE],
    fileActions: [RECORD.DOWNLOAD, RECORD.IMPORT, RECORD.MOVE],
    rowActions: [
      RECORD.ASSIGN,
      RECORD.CHAT,
      RECORD.QUICK_CALL,
      RECORD.ADD_COMMENT,
      RECORD.SEND_TEMPLATE,
      RECORD.VIEW_STATUS_LOGS,
      RECORD.VIEW_IMAGES,
    ],
  },
  manager: {
    crud: [RECORD.VIEW, RECORD.CREATE, RECORD.UPDATE, RECORD.DELETE],
    fileActions: [RECORD.DOWNLOAD, RECORD.IMPORT, RECORD.MOVE],
    rowActions: RECORD_ROW_ACTION_KEYS.map((item) => item.key),
  },
};

function authzFromGrants(grants) {
  return {
    isFullAccess: false,
    isAuthzV2Enabled: true,
    grants,
  };
}

export function getEffectiveActionsForCampaign(grants, campaignId, accountId) {
  return getEffectiveRecordActions(authzFromGrants(grants), campaignId, accountId);
}

export function isCustomRecordConfig(grants, campaignId) {
  return campaignHasExplicitRecordGrants(authzFromGrants(grants), campaignId);
}

export function buildExplicitActionsMap(enabledKeys = []) {
  const actions = {};
  [...RECORD_CRUD_KEYS, ...RECORD_FILE_ACTION_KEYS, ...RECORD_ROW_ACTION_KEYS].forEach(({ key }) => {
    actions[key] = enabledKeys.includes(key);
  });
  return actions;
}

export function upsertRecordActionsForCampaign(grants, campaignId, actionsPatch) {
  const campaignKey = String(campaignId);
  const next = { ...(grants.record_actions || {}) };
  const current = { ...(next[campaignKey] || {}) };
  const merged = { ...current, ...actionsPatch };

  Object.keys(merged).forEach((key) => {
    if (!merged[key]) {
      delete merged[key];
    }
  });

  if (Object.keys(merged).length === 0) {
    delete next[campaignKey];
  } else {
    next[campaignKey] = merged;
  }

  return {
    ...grants,
    record_actions: next,
  };
}

export function clearRecordActionsForCampaign(grants, campaignId) {
  const campaignKey = String(campaignId);
  const next = { ...(grants.record_actions || {}) };
  delete next[campaignKey];
  return {
    ...grants,
    record_actions: next,
  };
}

export function applyPresetToCampaign(grants, campaignId, presetKey) {
  const preset = RECORD_PRESETS[presetKey];
  if (!preset) {
    return grants;
  }
  const enabledKeys = [
    ...preset.crud,
    ...(preset.fileActions || []),
    ...preset.rowActions,
  ];
  return upsertRecordActionsForCampaign(
    grants,
    campaignId,
    buildExplicitActionsMap(enabledKeys)
  );
}

export function normalizeRecordActionsOnViewOff(grants, campaignId) {
  return upsertRecordActionsForCampaign(
    grants,
    campaignId,
    buildExplicitActionsMap([])
  );
}

export function hasCrossModuleWarning(grants, actionKey) {
  const rowDef = RECORD_ROW_ACTION_KEYS.find((item) => item.key === actionKey);
  if (!rowDef?.crossModule) {
    return false;
  }
  const features = grants?.features || {};
  if (rowDef.crossModule === 'whatsapp') {
    return !features[AUTHZ_FEATURES.WHATSAPP.SEND_TEMPLATE];
  }
  if (rowDef.crossModule === 'intervene') {
    return !features[AUTHZ_FEATURES.INTERVENE.CHAT];
  }
  return false;
}
